<template>
  <div class="app-body">
    <Header></Header>
    <main class="container-fluid">
      <div class="row">
        <div class="col-sm-12 col-md-6 offset-md-3 col-text">
          <h1>Privacy Policy</h1>
          <p><i>Effective Since 08 May 2020</i>.</p>

          <p>This page provides an overview of GanttProject Cloud Privacy Policy during the beta testing period. The policy may change when
            GanttProject Cloud graduates from beta. We will notify users who are involved into the beta testing about the
            changes, if any, by all possible means. Some of the terms which will become effective after the graduation from beta are
            marked with <span class="badge badge-pill badge-info">post-beta</span> badge.</p>

          <p>This document describes how we collect, use and handle your personal data when you use our service.</p>
          <br>
          <p><b>Short version:</b> we do not share or disclose your personal information unless you make it public or
            disclosure is enforced by law. We never sell your personal data to third parties for any purposes. You
            have the right to download your personal data and delete it completely at any moment.</p>
          <br>
          <h4>Definitions</h4>
          <dl>
            <dt>The Website</dt>
            <dd><p>The "Website" refers to GanttProject Cloud web site, located at <a href="https://ganttproject.cloud">ganttproject.cloud</a>,
              with all its subdomains, and all content and services.</p>
            </dd>

            <dt>Service</dt>
            <dd><p>The “Service” refers to the applications, software, products, and services provided by GanttProject Cloud.</p></dd>

            <dt>"GanttProject Cloud", "We" and "Us"</dt>
            <dd><p>"GanttProject Cloud", "We" and "Us" refer to BarD Software s.r.o., a privately held company
              registered in Czech Republic, as well as our affiliates, directors, subsidiaries, contractors, licensors,
              officers, agents, and employees.</p></dd>

            <dt>“The User,” “You,” and “Your”</dt>
            <dd><p>“The User,” “You,” and “Your” refer to the individual person, company, or organization that has visited or is
              using the Website or Service; that accesses or uses any part of the account; or that directs the use of the
              account in the performance of its functions.</p></dd>

            <dt>Your Content</dt>
            <dd>Your Content is any text or binary data which You make available to us by means of Website or other parts
              of Service and which is stored under any team owned by you.</dd>
          </dl>

          <h4>What personal data we collect</h4>
          <dl>
            <dt>Web browser information</dt>
            <dd>
              <p>When you are browsing the Website, we collect the same basic information that most websites
                collect. We use common Internet technologies, such as cookies and web server logs. We collect
                browser information from every visitor of the Website, whether they have an account or not.</p>

              <p>The information we collect about all visitors of the Website includes the visitor’s IP address,
                browser type, language preference, referring site, and the date and time of each visitor
                request. </p>

              <p>We collect this information with the following purposes:</p>
              <ul>
                <li>to better understand the patterns of usage of the Website</li>
                <li>to monitor and protect the Website</li>
                <li>to solve production issues should they happen</li>
              </ul>

              <p>The collected information is stored in raw format as produced by the web server for up to 30 days
                and is removed afterwards.</p>
            </dd>

            <dt>Authentication Data</dt>
            <dd><p>We delegate authentication data processing to
              <a href="https://firebase.google.com">Google Firebase Auth</a>. As a data collector, we have access
              to basic profile information, however, we do not have access to any sensitive data, such as
              passwords used in email authentication or passwords from third party services, such as Google or
              Facebook.</p>
            </dd>

            <dt>User Account Information</dt>
            <dd>
              <p>If you create an account, we ask for basic information about you from the authentication service.
                At the bare minimum we ask for your display name and email. Some third-party authentication
                services, such as Google or Facebook, may provide us with additional profile data, however, we
                always request the minimal amount of information which is supported by the authentication
                service.
              </p>
              <p>
                We collect account information with the following purposes:
              </p>
              <ul>
                <li>to create your account, and to provide the requested services;</li>
                <li>to fill out your profile and share that profile with other users if you ask us to;</li>
                <li>to communicate with you, if you've said that's okay, and only for the reasons you’ve said
                  that’s okay. Please see our section on email communication for more information.</li>
              </ul>
              <p>
                User Account Information does not include aggregated, non-personally identifying information. We
                may use aggregated, non-personally identifying information to operate, improve, and optimize our
                Website and Service.
              </p>

              <p>User account information is removed from the Website immediately upon account removal but may be
                stored in backups for up to 180 days after account removal or for other longer period if
                required by law.</p>
              <p>Use your dashboard on the Website to check what user information is on our records.</p>
            </dd>

            <dt>Your Content</dt>
            <dd>
              <p>The purpose of our Service is to make it simple for you to work on your documents, collaborate
                with others, and work across multiple devices. To make that possible, we store, process, and
                transmit Your Content as well as information related to it. The related information includes the
                list of your collaborators, as well as things like the size of the file, the time it was
                uploaded, etc. This information may be stored and processed by our Service Providers on our
                behalf, as defined below.</p>

              <p>For the purposes of personal data control, Your Content is any data submitted or uploaded by any
                member of any team owned by you.</p>
              <p><b>Shared Content</b></p>
              <p>When you invite someone into the team, you grant them access to certain bits of information. You
                may grant access to private project by sending explicit invitations from the Website user
                interface or by sending an access link via third-party communication channels.</p>
              <p>If you send explicit invitation we deliver it to the recipient by email which you provide to us.
                It is your responsibility to provide us a valid email of the person with whom you are sharing
                the project.</p>
              <p>If you create an access link, you can send it to anyone via any communication channel. The link
                remains valid after its use or until you cancel the invitation.</p>
              <p>If you add any data to any team where you are not the owner, you transfer the ownership of that
                data, for the purposes of personal data control process, to the owner of the team.</p>
            </dd>

            <dt>Usage information</dt>

            <dd><p>We collect information related to your usage of the Services, including your interactions with
              the interface. We use this information to improve our Service, develop new services and features,
              measure performance, debug production issues and protect users. </p>

              <p><b>Short Term Usage History</b></p>
              <p>We may record and keep personally identifiable bits of usage information for a short period for
                monitoring, debugging and other production maintenance purposes. These records are automatically
                removed from the storage in 24 hours after recording.</p>

              <p><b>Long Term Usage History</b></p>
              <p>We collect usage information with the help of Google Analytics service where it is stored and
                processed in anonymized and aggregated form. We comply with its <a
                    href="https://www.google.com/analytics/terms/us.html" target="_blank"> terms and
                  policies</a>. In particular, we do not use any advertising features of Google Analytics.
              </p>
            </dd>
          </dl>

          <h4>What personal data we DO NOT collect</h4>
          <p>We do not collect, store or write to any sort of logs your financial information, such as credit card
            numbers, your passwords from third-party authentication services, such as Google or Facebook, and any
            other personal data except for the listed above.
          </p>
          <p>
            We do not intentionally collect information that is stored in your projects or other data which you make
            available to us by using our service. Information in your projects belongs to you, and you are
            responsible for it, as well as for making sure that your content complies with our <a
              :href="routes.ABOUT_TOS">Terms of Service</a>. Our employees do not access your stuff unless it
            is required for security or maintenance, or for support reasons, with the consent of the data owner.
          </p>

          <h4>Where we store personal data and with whom we may share it</h4>
          <dl>
            <dt>Service Providers</dt>
            <dd>
              <p>
                We use services provided by certain trusted third party companies and individuals, referred
                hereafter as Service Providers, to provide the necessary technologies required to run and
                improve our Service, including, but not limited to hardware, software, networking, storage,
                analytics, customer support, processing credit card payments, etc. Service Providers may have
                access to or process your information for the purpose of providing their services for us. They
                are not permitted to use your information for their own purposes.</p>

              <p>Below you can find the list of our third-party vendors as of 08 May 2020, with the links to their
                privacy and/or security pages.</p>

              <ul>
                <li>Google Cloud Platform (GCP) ran by Google LLC is the major vendor providing most of
                  resources required to run our Service. See GCP
                  <a href="https://cloud.google.com/security/">security</a> and
                  <a href="https://cloud.google.com/security/gdpr/">GDPR compliance</a> pages.</li>
                <li>Firebase Application Platform, which is owned by Google, is our authentication provider.
                  See <a class="d-block" href="https://firebase.google.com/support/privacy/">Firebase privacy policy</a></li>
                <li>Mailgun services, ran by Mailgun Technologies, Inc. is our outgoing email service provider.
                  See Mailgun
                  <a href="https://www.mailgun.com/privacy-policy">privacy policy</a>
                  and <a href="https://www.mailgun.com/gdpr">GDPR pages</a>.</li>
              </ul>
            </dd>

            <dt>Data Storage Location</dt>
            <dd><p>We store all data which is made available to the Service in Google Cloud data centers in the
              United States. However, service providers and business partners may be located anywhere in the
              world.</p></dd>

            <dt>Data Sharing and Disclosure</dt>
            <dd><p>We may share your data with third-party Service Providers as defined above. These third parties
              may have access to your information only for the purposes of performing these tasks on our behalf
              and under obligations similar to those in this Privacy Policy.</p>

              <p>We may disclose personally-identifying information or other information we collect about you to
                law enforcement in response to a valid legal request, or when we believe in good faith that
                disclosure is reasonably necessary to protect our property or rights, or those of third parties
                or the public at large.</p>

              <p>In complying with legal processes we will make a reasonable effort to notify users of any
                disclosure of their information, unless we are prohibited by law or court order from doing
                so.</p>
            </dd>
          </dl>

          <h4>Personal Data Control and Removal</h4>
          <dl>
            <dt>Changing Personal Data&nbsp;<span class="badge badge-pill badge-info">post-beta</span></dt>
            <dd>
              <p>You can change and remove your personal data from your dashboard.</p>
            </dd>
            <dt>Exporting Your Content</dt>
            <dd>
              <p>You can download Your Stuff with the help of export function on the dashboard page.</p>
            </dd>
            <dt>Data Removal</dt>
            <dd>
              <p>If you want to remove your account and all associated personal data, please send a request to
                <code>{{emails.CONTACT}}</code>. We process account removal requests within 72 hours.</p>
              <p>When account is deleted we remove all data owned by the account user from the Website. This
                includes all the owned teams and all the contents under the owned teams, however it does not
                include the contents which was created in teams owned by other users.</p>
              <p>Data which is deleted from serving may reside in backups for up to 180 days after account removal
                or for other longer period if required by law.</p>
            </dd>
          </dl>

          <h4>Use of cookies and tracking</h4>
          <dl>
            <dt>Cookies and Local Storage</dt>
            <dd>
              <p>Cookie is a small piece of text data which is stored in your web browser and associated with a
                website. Cookies are passed from the browser to our Website every time when you visit Website
                pages. We use cookies for i) authentication and authorization, and ii) collecting anonymized
                usage analytics.
              </p>

              <p>Authentication cookies are created after you sign in and are cleared when you sign out. They are
                essential for our service operations and you may not opt-out.</p>
              <p>Analytics cookies of the second type are set on page visit. They are anonymized and do not
                require user consent. However, you may opt-out from using them in the cookie settings
                dialog.</p>

              <p>Local storage is a way to store some information associated with a website in your browser. Data
                stored in local storage never leaves your browser but is accessible to code running in your
                browser when you visit our Website. We may use local storage to improve your user experience, in
                particular for caching your authentication methods.</p>
              <p>By using GanttProject Cloud you express your consent to store cookies in your web browser and
                transfer them to our Website and to use local storage.</p>
            </dd>
          </dl>

          <h4>Email communication</h4>
          <dl>
            <dt>Email addresses collection</dt>
            <dd>
              <p>We may collect your email account in a few ways: i) you use email-based authentication; ii) you
                set your contact email in the settings; iii) your collaborator shares projects with you by
                sending invitation link through our mail server.</p>
            </dd>
            <dt>Email use</dt>
            <dd>
              <p>We may send you transactional emails in the following cases:</p>
              <ul>
                <li>Other GanttProject Cloud user sends you an invitation link to join their team by email. In
                  case of abusing this feature we ask you to inform us through the
                  <a :href="routes.ABOUT_CONTACTS">support email</a>.
                </li>
                <li>You request notifications on some events, such as credit falling below some threshold. You
                  have to opt-in into receiving such notifications.
                </li>
                <li>We need some assistance from you to resolve production issue (e.g. if
                  there is suspicious traffic coming on your behalf).
                </li>
                <li>You request password reset link.</li>
              </ul>
              <p>We may send you promotional marketing emails if you explicitly opt-in into receiving such
                emails.</p>
            </dd>

          </dl>

          <h4>Links to Third Party Websites</h4>
          <p>Our Website may contain links to other websites which are outside our control and are not covered by this
            Privacy Policy (“Third-Party Websites”). If you access Third-Party Websites using the links provided,
            the operators of such sites may collect information from you which will be used by them in accordance
            with their own privacy policies, which may differ from ours. We encourage you to review the privacy
            policies of those Third-Party Websites so that you understand if/how they collect and/or use information
            from you or your computer.
          </p>


          <h4>Security Practices</h4>
          <dl>
            <dt>Communications</dt>
            <dd><p>All data exchanged with our Website is always transmitted over secure channels.</p></dd>

            <dt>Passwords</dt>
            <dd><p>We do not store and do not even access any passwords, including passwords from third-party
              services and passwords used for email authentication.</p></dd>

            <dt>File System and Backups</dt>
            <dd>
              <p>Most of the data available from the Website is stored in Google Cloud Storage and Google Cloud
                Datastore. Data stored at Google is always
                <a href="https://cloud.google.com/storage/docs/encryption/">encrypted at rest</a>
                and is always transmitted via secure channels.
              </p>
            </dd>

            <dt>System Security</dt>
            <dd><p>Most of the components involved into the Service operations run in isolated containers with the
              least possible privileges.</p></dd>

            <dt>Software Security</dt>
            <dd><p>Every change to the codebase passes through code review which includes checking security aspects,
              such as
              possible XSS/CSRF attacks, injections, etc. We occasionally conduct security audit by third-party
              partners.</p></dd>
          </dl>

          <h4>Changes</h4>
          <p>If we are involved in a reorganization, merger, acquisition, or sale of our assets, your information may
            be transferred as part of that deal. We will notify you (for example, via a message to the email address
            associated with your account) of any such deal and outline your choices in that event.</p>

          <p>We may revise this Privacy Policy from time to time, and will post the most current version on our
            website. If a revision meaningfully reduces your rights, we will notify you.</p>

          <h4>Contact</h4>
          <p>GanttProject Cloud is operated by
            BarD Software s.r.o., a limited liability company registered
            in Czech Republic, which is a controller of your personal
            data. Should you have any questions on privacy policies or on your personal data, please contact us by
            email <code>{{emails.PRIVACY}}</code>.</p>
        </div>

        <div class="col-sm-12 col-md-3">
          <b>Related Links</b>
          <a class="d-block" href="https://firebase.google.com/support/privacy/">Firebase privacy policy</a>
          <a class="d-block" href="https://cloud.google.com/security/">Google Cloud Security</a>
          <a class="d-block" href="https://cloud.google.com/security/gdpr">Google Cloud GDPR</a>
          <a class="d-block" href="https://www.google.com/analytics/terms/us.html" target="_blank">Google Analytis
            Terms and Policies</a>
          <a class="d-block" href="https://support.google.com/analytics/topic/2919631" target="_blank">Google
            Analytics Data Privacy and Security</a>
          <a class="d-block" href="https://www.mailgun.com/privacy-policy">Mailgun privacy policy</a>
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import Header from '@/components/Header.vue';

@Component({
  components: {Header}
})
export default class AboutPrivacy extends Vue {
  private routes = {
      ABOUT_TOS: "/about/tos",
  };
  private emails ={
      CONTACT: "contact@ganttproject.cloud"
  };
}
</script>

<style lang="scss">
@import 'sass/about_privacy';

</style>
